import { useRouter } from "next/router";
import { useCallback } from "react";

import { config } from "shared/data";
import { isEnabledClientRoute, isValidUrl, notEmpty } from "shared/lib/utils";

import { routes } from "scmp-app/lib/router";
import { shouldRedirectBaseDynamicSectionLink } from "scmp-app/lib/router/helpers";

import { RouteType, useRouteDetector } from "./route-detector";

type RouteOptions = {
  applicationId?: string;
  entityId?: string;
  entityUuid?: string;
  query?: Record<string, string>;
  type?: string;
};

export const useRouteHandler = () => {
  const { getRouteTypeByRouteEntity, getUrlAliasByUrl } = useRouteDetector();
  const router = useRouter();

  const buildUrlWithQuery = (url: string, query: RouteOptions["query"]) => {
    if (!notEmpty(query)) return url;
    try {
      const isFullUrl = isValidUrl(url);
      const urlObject = isFullUrl ? new URL(url) : new URL(url, window.location.origin);
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined) urlObject.searchParams.set(key, value);
      });
      return urlObject.toString();
    } catch {
      return url;
    }
  };

  return useCallback(
    (url: string, { applicationId, entityId, query, type }: RouteOptions) => {
      const urlAlias = getUrlAliasByUrl(url);
      const handleOpenPage = (target = "_blank") => {
        window.open(urlAlias, target);
      };
      if (!isEnabledClientRoute(url)) {
        location.href = buildUrlWithQuery(url, query);
        return;
      } else if ([config.account.scmpAccountHost].some(host => url.includes(host)))
        return handleOpenPage("_self"); // Special handling for internal hosts

      const routeType = getRouteTypeByRouteEntity({
        applicationId,
        routeType: type,
        urlAlias,
      });
      switch (routeType) {
        case RouteType.Dynamic: {
          const matched = routes.dynamics[type as string];
          if (!matched || !entityId) return;
          const matchedUrl = matched.route(entityId);

          if (shouldRedirectBaseDynamicSectionLink(matchedUrl.pathname, query)) {
            location.href = buildUrlWithQuery(url, query);
            return;
          }

          return router.push(matchedUrl, {
            pathname: urlAlias,
            query,
          });
        }
        case RouteType.Static: {
          // Not direct use url because need to support search route (/search/:value?)
          // Exact match the url first, if not found, try to find the url by prefix
          const key =
            Object.keys(routes.statics).find(staticRoute => staticRoute === url) ??
            Object.keys(routes.statics).find(staticRoute => staticRoute.startsWith(url));
          if (!key) return;
          const matched = routes.statics[key];
          if (!matched) return;
          const matchedUrl = matched({ params: {}, query: query ?? {} });
          return router.push(
            matchedUrl,
            matchedUrl.asPath ?? {
              pathname: urlAlias,
              query,
            },
          );
        }
        default:
          return handleOpenPage();
      }
    },
    [getRouteTypeByRouteEntity, getUrlAliasByUrl, router],
  );
};
